import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { getLiteral } from 'utils/getLiteral';

const DELETE_SYNC_LITERAL = 'label_helptext_delete_sync_records';

export const isRecordReadOnly = (data) =>
    getBackendBoolean(data?.isReadOnly) ||
    getBackendBoolean(data?.readOnlyCompany) ||
    getBackendBoolean(data?.readOnly);

/**
 * Backend connector CanDelete from the sync ticket (Sage).
 * Does not read client-side flags such as ActivityModel activity-type canDelete.
 *
 * @param {object} data
 * @param {{ fromMappedRecord?: boolean }} options
 *   fromMappedRecord — when true (sync entities after model mapping), lowercase
 *   canDelete on the record is the backend value mapped from CanDelete/canDelete.
 */
/**
 * Detail/list API payloads may expose sync CanDelete as lowercase `canDelete`
 * without SyncStatusId (e.g. companies detail). Coalesce into CanDelete first.
 */
export const getBackendSyncCanDeleteFromApi = (data) =>
    getBackendSyncCanDelete({
        ...data,
        CanDelete: data?.CanDelete ?? data?.canDelete,
    });

export const getBackendSyncCanDelete = (data, { fromMappedRecord = false } = {}) => {
    if (data?.CanDelete != null) {
        return getBackendBoolean(data.CanDelete);
    }

    if (fromMappedRecord && data?.canDelete != null) {
        return getBackendBoolean(data.canDelete);
    }

    if (
        data?.canDelete != null &&
        (data?.syncStatusId != null || data?.SyncStatusId != null || data?.syncstatusid != null)
    ) {
        return getBackendBoolean(data.canDelete);
    }

    return true;
};

/**
 * readOnly first, then backend sync CanDelete when applySyncCanDelete is true.
 */
export const getDeleteRestriction = ({ data, hasDeletePermission, applySyncCanDelete = false }) => {
    if (!hasDeletePermission || !data?.id) {
        return { visible: false, enabled: false, tooltip: null };
    }

    if (isRecordReadOnly(data)) {
        return { visible: false, enabled: false, tooltip: null };
    }

    if (applySyncCanDelete && !getBackendSyncCanDelete(data, { fromMappedRecord: true })) {
        return {
            visible: true,
            enabled: false,
            tooltip: getLiteral(DELETE_SYNC_LITERAL),
        };
    }

    return { visible: true, enabled: true, tooltip: null };
};

/**
 * Entity-specific delete visibility unrelated to sync CanDelete (e.g. activity type).
 */
export const shouldHideLegacyClientDelete = (data) =>
    data?.hasOwnProperty('canDelete') && !data.canDelete;
